import React, { useState, useRef } from 'react';
import { useLinesContext } from '../../../contexts/Lines';
import { noOverflow } from '../../../themes/styles';
import Button from '../../BuildingBlocks/Button/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';

const useStyles = makeStyles((theme: any) => ({
  root: { paddingLeft: theme.spacing(1), paddingRight: theme.spacing(1) },
  leftButton: {
    width: `80%`,
    justifyContent: 'start',
  },
  rightButton: {
    width: `20%`,
  },
  button: {
    height: 40,
    border: `1px solid ${theme.palette.primary.main}`,
    marginLeft: 0,
    backgroundColor: theme.palette.brand.white,
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[0],
    '&:hover': {
      backgroundColor: theme.palette.brand.blueGrey,
    },
  },
  menuItem: {
    '&:hover': {
      backgroundColor: theme.palette.brand.blueGrey + ' !important',
    },
    backgroundColor: theme.palette.brand.white + ' !important',
    fontSize: 14,
  },
  menuItemBorderBottom: {
    borderBottom: `1px solid ${theme.palette.primary.main}`,
  },
  buttons: {
    display: 'flex',
    opacity: ({ lineOnEditMode }: any) => (lineOnEditMode ? '70%' : '100%'),
  },
  logo: {
    marginRight: theme.spacing(1),
  },
  text: {
    fontWeight: 600,
    fontSize: '14px',
    color: theme.palette.primary.main,
    ...(noOverflow as object),
  },
  selectorButton: {
    transform: ({ open }) => (open ? 'rotate(180deg)' : ''),
    justifyContent: 'center',
    marginLeft: 0,
  },
  popperContainer: {
    zIndex: theme.zIndex.tooltip + 1,
  },
  paperBackground: {
    background: theme.palette.brand.white,
    border: `1px solid ${theme.palette.primary.main}`,
    marginTop: theme.spacing(1),
  },
}));

export default function NewLineButton({
  productId,
  lineOnEditMode,
}: {
  productId: string;
  lineOnEditMode: boolean;
}) {
  const { createLineClick, autoCreateLinesClick, lines } = useLinesContext();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [popGeneratedialogOnes, setPopGenerateDialogOnes] = useState(false);
  const anchorRef = useRef(null);
  const classes = useStyles({ open, lineOnEditMode });

  const options = [
    {
      text: 'Add New Line',
      onClick: () => {
        const linesNumber = lines.filter(
          (l) => !l.new && l.product === productId,
        );
        if (!popGeneratedialogOnes && linesNumber.length === 1) {
          autoCreateLinesClick(productId, true);
          setPopGenerateDialogOnes(true);
        } else createLineClick(productId);
      },
      icon: 'icons/editBlue.svg',
    },

    {
      text: 'Generate Lines with AI',
      onClick: () => autoCreateLinesClick(productId),
      icon: 'icons/brain.svg',
    },
  ];

  const handleMenuItemClick = (index: number) => {
    setSelectedIndex(index);
    setOpen(false);
    options[index].onClick();
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <div className={classes.root}>
      <ButtonGroup
        ref={anchorRef}
        aria-label="split button"
        className={classes.buttons}
        disabled={lineOnEditMode}
      >
        <Button
          useWrapper={false}
          className={classes.button + ' ' + classes.leftButton}
          fullWidth={true}
          onClick={() => options[selectedIndex].onClick()}
        >
          <img
            src={options[selectedIndex].icon}
            alt={options[selectedIndex].text}
            className={classes.logo}
          />
          <Typography className={classes.text}>
            {options[selectedIndex].text}
          </Typography>
        </Button>

        <Button
          className={classes.button + ' ' + classes.rightButton}
          useWrapper={false}
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <img
            src={'icons/arrowMenuOpenerBlue.svg'}
            alt={'Arrow menu opener'}
            className={classes.selectorButton}
          />
        </Button>
      </ButtonGroup>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        placement={'right-start'}
        className={classes.popperContainer}
      >
        <Paper className={classes.paperBackground}>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList>
              {options.map((option, index) => (
                <MenuItem
                  key={option.text}
                  selected={index === selectedIndex}
                  onClick={() => handleMenuItemClick(index)}
                  className={
                    classes.menuItem +
                    ' ' +
                    (index === 0 && classes.menuItemBorderBottom)
                  }
                >
                  <img
                    src={option.icon}
                    alt={option.text}
                    className={classes.logo}
                  />
                  {option.text}
                </MenuItem>
              ))}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </div>
  );
}
